import React, { useEffect, useState } from 'react';
import copy from 'copy-to-clipboard';
import styled from 'styled-components';

import {
  FlexRow,
  Link,
  StyledTableRow,
  SvgIcon,
  useMatchMedia,
} from '@make-software/cspr-ui';
import { BodyText } from '@block-explorer/ui';

const StyledContainer = styled.span<{ isCopied: boolean }>`
  ${({ theme, isCopied }) => ({
    lineHeight: '20px',
    cursor: 'pointer',
    position: 'relative',
    pointerEvents: isCopied ? 'none' : 'initial',
    '>:not(:first-child)': {
      marginLeft: 8,
    },
    svg: {
      color: isCopied
        ? theme.styleguideColors.contentGreen
        : theme.styleguideColors.contentTertiary,
      ':hover': {
        color: theme.styleguideColors.fillPrimaryRed,
      },
      ':active': {
        color: theme.styleguideColors.fillPrimaryRedClick,
      },
      ...(isCopied && {
        animation: 'fadeInOut 3s',
      }),
    },
  })}

  ${StyledTableRow} & {
    visibility: hidden;
  }
  ${StyledTableRow}:hover & {
    visibility: visible;
  }
`;

const StyledBodyText = styled(BodyText)(({ theme }) => ({
  position: 'absolute',
  left: 12,
  animation: 'fadeInOut 3s',
  color: theme.styleguideColors.contentGreen,
  svg: {
    color: theme.styleguideColors.contentGreen,
  },
}));

/* eslint-disable-next-line */
export interface CopyButtonProps {
  value?: string | null;
  type?: 'full' | 'tiny';
  message?: string;
  label?: string;
}

export function CopyButton({
  message = 'Copied!',
  type = 'tiny',
  value,
  label,
}: CopyButtonProps) {
  const [isCopied, setIsCopied] = useState<boolean>(false);

  useEffect(() => {
    let timer;
    if (isCopied) {
      timer = setTimeout(() => {
        setIsCopied(false);
      }, 2900);
    }
    return () => clearTimeout(timer);
  }, [isCopied]);

  const copiedMessage = useMatchMedia([null, null, null, message], []);

  const handleClick = (ev) => {
    ev.stopPropagation();

    if (value) {
      copy(value);
      setIsCopied(true);
    }
  };

  if (value == null) {
    return null;
  }

  return (
    <FlexRow align={'center'} justify={'flex-start'} itemsSpacing={8}>
      {label && (
        <Link color={'primaryBlue'} onClick={handleClick}>
          <BodyText size={2}>{label}</BodyText>
        </Link>
      )}
      <StyledContainer onClick={handleClick} isCopied={isCopied}>
        {!isCopied ? (
          <SvgIcon src="assets/icons/ic-copy.svg" size={16} />
        ) : (
          <>
            <SvgIcon src="assets/icons/ic-success.svg" size={16} />
            {type === 'full' && (
              <StyledBodyText size={2}>{copiedMessage}</StyledBodyText>
            )}
          </>
        )}
      </StyledContainer>
    </FlexRow>
  );
}

export default CopyButton;
